import { DialogComponent } from '@theme/dialog';

/**
 * Deja Studio Size Finder UI. The fit logic lives in size-finder-engine.js and is
 * only downloaded the first time the panel opens, so product pages pay nothing for it.
 *
 * The result leads with one size and one sentence; the zone-by-zone analysis sits
 * behind "Why this size?" for shoppers who want it.
 *
 * Views are shown and hidden rather than re-rendered: DialogComponent closes the
 * dialog when a click target is no longer inside it, so a clicked button must
 * stay in the DOM.
 */

const STORAGE_KEY = 'deja:size-finder';
const PREFERENCES = ['tighter', 'as_designed', 'looser'];

const COPY = {
  en: {
    title: 'Find your size',
    chartTitle: 'Size chart',
    height: 'Height',
    weight: 'Weight',
    fitQuestion: 'How do you want it to fit?',
    preference: { tighter: 'Closer', as_designed: 'As designed', looser: 'Roomier' },
    // Fallbacks for profiles without their own hint_* text.
    preferenceHint: {
      tighter: 'A little closer to the body than this piece is cut.',
      as_designed: 'The look this piece was designed for.',
      looser: 'A little roomier than this piece is cut.',
    },
    submit: 'Show my size',
    fineprint: 'An estimate from height and weight. Your details stay on this device.',
    errors: {
      height: 'Enter your height in cm (120–230).',
      weight: 'Enter your weight in kg (30–250).',
    },
    suggested: 'Suggested size',
    closest: 'Closest size',
    headline: {
      belowRange: (s) => `We recommend size ${s}. It may look relaxed and roomy on you.`,
      aboveRange: (s) => `We recommend size ${s}, our largest. It may sit a little closer than the intended fit.`,
      unreachableRoomy: (s) => `Even ${s}, our smallest size, will be noticeably roomier than the fit you asked for.`,
      unreachableClose: (s) => `Even ${s}, our largest size, will be noticeably closer than the fit you asked for.`,
      unreachableStretch: (s) =>
        `${s} is our largest size and may be tight on you. Its stretch hasn't been measured, so we can't say for sure.`,
      asDesigned: (s, hint) => (hint ? `We recommend size ${s}. ${hint}` : `${s} fits the way it was designed.`),
      closerAsked: (s) => `${s} sits closer than designed, as you asked.`,
      roomierAsked: (s) => `${s} sits roomier than designed, as you asked.`,
      closer: (s) => `${s} sits a little closer than designed.`,
      roomier: (s) => `${s} sits a little roomier than designed.`,
    },
    notes: {
      soldOut: (s) => `${s} is sold out right now.`,
      checkChart: 'We suggest checking the size chart before you order.',
      stretchUnknown: "This piece's stretch hasn't been measured, so please check the size chart too.",
      smallestLong: (s) => `In ${s} the body may sit a little long.`,
      sleeveShort: 'For your height, the sleeves may sit a little short.',
      lengthShort: 'For your height, the body may sit a little short.',
      lengthLong: 'The body may sit a little long.',
      sleeveLong: 'The sleeves may run a little long.',
      ribCuff: 'The sleeves are cut long and gather at the ribbed cuff.',
      outsideModel: 'Your measurements are outside the range this estimate is tuned for, so check the size chart too.',
    },
    alternative: {
      looser: (s) => `Prefer it roomier? Size ${s}`,
      tighter: (s) => `Prefer it closer? Size ${s}`,
    },
    select: (s) => `Select size ${s}`,
    soldOutButton: (s) => `${s} is sold out`,
    why: 'Why this size?',
    whyIntro: (refs) =>
      `We compare your height and weight with how this piece was designed to sit (${refs}). These are estimates, not measurements.`,
    reference: (h, w, s) => `${h} cm / ${w} kg in ${s}`,
    rows: { chest: 'Fit', length: 'Body length', sleeve: 'Sleeves' },
    chestFit: {
      '-2': 'Much closer than designed',
      '-1': 'Closer than designed',
      0: 'As designed',
      1: 'Roomier than designed',
      2: 'Much roomier than designed',
    },
    lengthFit: { '-2': 'Runs short', '-1': 'A little short', 0: 'As designed', 1: 'A little long', 2: 'Runs long' },
    landmark: {
      midriff: 'Likely ends above your waist',
      waist: 'Likely ends around your waist',
      high_hip: 'Likely ends at the top of your hips',
      hip: 'Likely ends around your hips',
      low_hip: 'Likely ends just below your hips',
      thigh: 'Likely ends around your upper thigh',
    },
    edit: 'Edit details',
    startFinder: 'Find your size',
    chart: 'Size chart (cm)',
    columns: { size: 'Size', chest: 'Chest, flat', hem: 'Hem, flat', length: 'Length', sleeve: 'Sleeve', shoulder: 'Shoulder' },
    tolerance: (n) => `Measured flat. May vary by ±${n} cm due to washing and dyeing.`,
    disclaimer: 'Estimated from height and weight. Not a guarantee of fit.',
    forget: 'Forget my details',
    forgotten: 'Your details were cleared from this device.',
    noFitTitle: 'No clear fit',
    noFit: 'None of our sizes is likely to fit comfortably. Check the size chart, or contact us and we will help.',
    noData: 'We cannot suggest a size for this piece yet. Here is the size chart.',
  },
  tr: {
    title: 'Bedenini bul',
    chartTitle: 'Beden tablosu',
    height: 'Boy',
    weight: 'Kilo',
    fitQuestion: 'Nasıl durmasını istersin?',
    preference: { tighter: 'Daha oturan', as_designed: 'Tasarlandığı gibi', looser: 'Daha bol' },
    preferenceHint: {
      tighter: 'Bu parçanın kesiminden biraz daha vücuda yakın.',
      as_designed: 'Bu parçanın tasarlandığı görünüm.',
      looser: 'Bu parçanın kesiminden biraz daha bol.',
    },
    submit: 'Bedenimi göster',
    fineprint: 'Boy ve kilodan yapılan bir tahmindir. Bilgilerin yalnızca bu cihazda kalır.',
    errors: {
      height: 'Boyunu cm olarak gir (120–230).',
      weight: 'Kilonu kg olarak gir (30–250).',
    },
    suggested: 'Önerilen beden',
    closest: 'En yakın beden',
    headline: {
      belowRange: (s) => `${s} bedenini öneriyoruz. Üzerinde rahat ve bol bir görünüm sunabilir.`,
      aboveRange: (s) =>
        `${s} bedenini öneriyoruz. En büyük bedenimiz olduğu için tasarlanan görünümden biraz daha oturan durabilir.`,
      unreachableRoomy: (s) => `En küçük bedenimiz ${s} bile istediğin kalıptan belirgin şekilde bol duracak.`,
      unreachableClose: (s) => `En büyük bedenimiz ${s} bile istediğin kalıptan belirgin şekilde dar duracak.`,
      unreachableStretch: (s) =>
        `${s} en büyük bedenimiz ve sana dar gelebilir. Esneme payı ölçülmediği için kesin bir şey söyleyemiyoruz.`,
      asDesigned: (s, hint) => (hint ? `${s} bedenini öneriyoruz. ${hint}` : `${s} tasarlandığı gibi durur.`),
      closerAsked: (s) => `${s}, istediğin gibi tasarlanandan daha oturan durur.`,
      roomierAsked: (s) => `${s}, istediğin gibi tasarlanandan daha bol durur.`,
      closer: (s) => `${s} tasarlanandan biraz daha oturan durur.`,
      roomier: (s) => `${s} tasarlanandan biraz daha bol durur.`,
    },
    notes: {
      soldOut: (s) => `${s} beden şu an tükendi.`,
      checkChart: 'Sipariş vermeden önce beden tablosuna göz atmanı öneririz.',
      stretchUnknown: 'Bu modelin esneme payı ölçülmediği için beden tablosuna da göz atmanı öneririz.',
      smallestLong: (s) => `${s} bedende gövde biraz uzun durabilir.`,
      sleeveShort: 'Boyuna göre kollar biraz kısa kalabilir.',
      lengthShort: 'Boyuna göre gövde biraz kısa kalabilir.',
      lengthLong: 'Gövdede biraz uzun durabilir.',
      sleeveLong: 'Kollar biraz uzun gelebilir.',
      ribCuff: 'Kollar uzun kesildi ve ribanalı manşette toplanır.',
      outsideModel: 'Ölçülerin bu tahminin en iyi çalıştığı aralığın dışında; beden tablosuna da göz at.',
    },
    alternative: {
      looser: (s) => `Daha bol mu istersin? ${s} beden`,
      tighter: (s) => `Daha oturan mı istersin? ${s} beden`,
    },
    select: (s) => `${s} bedeni seç`,
    soldOutButton: (s) => `${s} tükendi`,
    why: 'Neden bu beden?',
    whyIntro: (refs) =>
      `Boy ve kilonu, bu parçanın tasarlandığı duruşla karşılaştırıyoruz (${refs}). Bunlar ölçüm değil, tahmindir.`,
    reference: (h, w, s) => `${h} cm / ${w} kg için ${s}`,
    rows: { chest: 'Kalıp', length: 'Gövde boyu', sleeve: 'Kollar' },
    chestFit: {
      '-2': 'Tasarlanandan çok daha oturan',
      '-1': 'Tasarlanandan daha oturan',
      0: 'Tasarlandığı gibi',
      1: 'Tasarlanandan daha bol',
      2: 'Tasarlanandan çok daha bol',
    },
    lengthFit: { '-2': 'Kısa kalır', '-1': 'Biraz kısa', 0: 'Tasarlandığı gibi', 1: 'Biraz uzun', 2: 'Uzun gelir' },
    landmark: {
      midriff: 'Tahminen belin üstünde biter',
      waist: 'Tahminen bel hizanda biter',
      high_hip: 'Tahminen kalçanın üst kısmında biter',
      hip: 'Tahminen kalça hizanda biter',
      low_hip: 'Tahminen kalçanın biraz altında biter',
      thigh: 'Tahminen üst bacak hizanda biter',
    },
    edit: 'Bilgileri düzenle',
    startFinder: 'Bedenini bul',
    chart: 'Beden tablosu (cm)',
    columns: { size: 'Beden', chest: 'En', hem: 'Alt genişlik', length: 'Boy', sleeve: 'Kol', shoulder: 'Omuz' },
    tolerance: (n) => `Düz yatırılarak ölçülmüştür. Yıkama ve boyama nedeniyle ±${n} cm fark edebilir.`,
    disclaimer: 'Boy ve kilodan yapılan bir tahmindir, kesin uyum garantisi değildir.',
    forget: 'Bilgilerimi unut',
    forgotten: 'Bilgilerin bu cihazdan silindi.',
    noFitTitle: 'Net bir beden yok',
    noFit: 'Bedenlerimizden hiçbiri muhtemelen rahat oturmayacak. Beden tablosuna göz at ya da bize yaz, yardımcı olalım.',
    noData: 'Bu parça için henüz beden öneremiyoruz. Beden tablosu aşağıda.',
  },
};

class SizeFinderComponent extends DialogComponent {
  /** @type {Promise<any> | null} */
  #engine = null;
  #profile = null;
  #rendered = false;
  #lastResult = null;

  get copy() {
    return COPY[this.dataset.locale] ?? COPY.en;
  }

  async openFinder() {
    await this.#prepare();
    this.#showView(this.#lastResult ? 'result' : 'form');
    this.showDialog();
  }

  async openChart() {
    await this.#prepare();
    this.#showView('chart');
    this.showDialog();
  }

  /** @param {SubmitEvent} event */
  async calculate(event) {
    event.preventDefault();
    const form = this.#view('form');
    const input = this.#readForm(form);
    if (!input) return;

    const engine = await this.#loadEngine();
    const availability = Object.fromEntries(this.#profile.sizes.map((s) => [s.label, s.available]));
    const result = engine.recommend(input, this.#profile, availability);

    if (result.status === 'invalid_input') {
      if (result.errors.includes('height_out_of_range')) this.#setError(form, 'height');
      if (result.errors.includes('weight_out_of_range')) this.#setError(form, 'weight');
      form.querySelector('[aria-invalid="true"]')?.focus();
      return;
    }

    this.#remember(input);
    this.#lastResult = result;
    this.#renderResult(result);
    this.#showView('result');
  }

  /** @param {Event} event */
  updateHint(event) {
    const form = this.#view('form');
    form.querySelector('[data-hint]').textContent = this.#hintFor(event.target.value);
  }

  editDetails() {
    this.#showView('form');
    this.#view('form').querySelector('input[name="height"]').focus();
  }

  /**
   * Selects the variant through the theme's own picker, so price, stock and buttons update as usual.
   * The panel closes first: the picker re-renders part of the section, which can interrupt the
   * closing animation if both run at once.
   */
  async selectSize(event) {
    const size = event.target.dataset.size;
    const scope = this.closest('.product-details') ?? this.closest('.shopify-section') ?? document;
    const picker = scope.querySelector('variant-picker');
    if (!picker || !size) return;

    await this.closeDialog();

    const radio = [...picker.querySelectorAll('input[type="radio"]')].find((input) => input.value === size);
    if (radio) {
      if (!radio.checked) radio.click();
    } else {
      const select = [...picker.querySelectorAll('select')].find((el) => [...el.options].some((o) => o.value === size));
      if (select) {
        select.value = size;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  forgetDetails(event) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    const form = this.#view('form');
    form.reset();
    form.querySelector('[data-hint]').textContent = this.#hintFor('as_designed');
    this.#lastResult = null;
    this.#showView('form');
    const status = form.querySelector('[data-status]');
    status.textContent = this.copy.forgotten;
    status.hidden = false;
    event.preventDefault();
  }

  async #prepare() {
    if (this.#rendered) return;
    this.#profile = this.#readProfile();
    const engine = await this.#loadEngine();
    const valid = this.#profile && engine.validateProfile(this.#profile).ok;
    this.refs.body.innerHTML = valid
      ? this.#formView() + '<div class="size-finder__view" data-view="result" hidden></div>' + this.#chartView()
      : this.#chartView(this.copy.noData);
    if (valid) this.#restore();
    this.#rendered = true;
  }

  #loadEngine() {
    this.#engine ??= import(this.dataset.engineUrl);
    return this.#engine;
  }

  /** Coerces the Liquid-rendered JSON into the shape the engine expects. */
  #readProfile() {
    try {
      const data = JSON.parse(this.refs.data.textContent);
      const num = (value) => (value == null || value === '' ? null : Number(value));
      return {
        ...data,
        tolerance: num(data.tolerance),
        referenceFits: data.referenceFits.map((ref) => ({
          height: num(ref.height),
          weight: num(ref.weight),
          size: ref.size,
          kind: ref.kind || 'ideal',
        })),
        sizes: data.sizes.map((size) => ({
          label: size.label,
          chestHalf: num(size.chestHalf),
          hemHalf: num(size.hemHalf),
          length: num(size.length),
          sleeve: num(size.sleeve),
          shoulder: num(size.shoulder),
          exists: size.exists,
          available: size.exists && size.available,
        })),
      };
    } catch {
      return null;
    }
  }

  #view(name) {
    return this.refs.body.querySelector(`[data-view="${name}"]`);
  }

  #showView(name) {
    for (const view of this.refs.body.querySelectorAll('[data-view]')) view.hidden = view.dataset.view !== name;
    const shown = this.#view(name) ? name : 'chart';
    if (shown !== name) this.#view('chart').hidden = false;
    this.refs.title.textContent = shown === 'chart' ? this.copy.chartTitle : this.copy.title;
    this.refs.body.scrollTop = 0;
  }

  #readForm(form) {
    const parse = (name) => Number(String(form.elements[name].value).replace(',', '.').trim());
    const height = parse('height');
    const weight = parse('weight');
    this.#clearErrors(form);

    if (!(height >= 120 && height <= 230)) this.#setError(form, 'height');
    if (!(weight >= 30 && weight <= 250)) this.#setError(form, 'weight');
    const firstInvalid = form.querySelector('[aria-invalid="true"]');
    if (firstInvalid) {
      firstInvalid.focus();
      return null;
    }

    return { height, weight, preference: form.elements.preference.value || 'as_designed' };
  }

  #setError(form, name) {
    const field = form.querySelector(`[data-field="${name}"]`);
    const input = form.elements[name];
    const error = field.querySelector('.size-finder__error');
    field.toggleAttribute('data-invalid', true);
    input.setAttribute('aria-invalid', 'true');
    error.textContent = this.copy.errors[name];
    error.hidden = false;
  }

  #clearErrors(form) {
    for (const field of form.querySelectorAll('[data-field]')) {
      field.removeAttribute('data-invalid');
      field.querySelector('input').removeAttribute('aria-invalid');
      field.querySelector('.size-finder__error').hidden = true;
    }
    form.querySelector('[data-status]').hidden = true;
  }

  #remember({ height, weight, preference }) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ height, weight, preference }));
    } catch {}
  }

  #restore() {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    } catch {}
    if (!saved) return;

    const form = this.#view('form');
    if (saved.height) form.elements.height.value = saved.height;
    if (saved.weight) form.elements.weight.value = saved.weight;
    if (PREFERENCES.includes(saved.preference)) {
      form.querySelector(`input[name="preference"][value="${saved.preference}"]`).checked = true;
      form.querySelector('[data-hint]').textContent = this.#hintFor(saved.preference);
    }
  }

  #hintFor(preference) {
    return this.#profile.hints?.[preference] || this.copy.preferenceHint[preference];
  }

  #formView() {
    const c = this.copy;
    const field = (name, unit) => `
      <div class="size-finder__field" data-field="${name}">
        <label class="size-finder__label" for="${this.#id(name)}">${c[name]}</label>
        <span class="size-finder__input-wrap">
          <input class="size-finder__input" id="${this.#id(name)}" name="${name}" type="text"
            inputmode="decimal" autocomplete="off" enterkeyhint="next"
            aria-describedby="${this.#id(name)}-error">
          <span class="size-finder__unit" aria-hidden="true">${unit}</span>
        </span>
        <span class="size-finder__error" id="${this.#id(name)}-error" role="alert" hidden></span>
      </div>`;
    const segments = PREFERENCES.map(
      (value) => `
        <label class="size-finder__segment">
          <input type="radio" name="preference" value="${value}"${value === 'as_designed' ? ' checked' : ''} on:change="/updateHint">
          <span>${c.preference[value]}</span>
        </label>`
    ).join('');

    return `
      <form class="size-finder__view" data-view="form" novalidate on:submit="/calculate">
        <div class="size-finder__measures">${field('height', 'cm')}${field('weight', 'kg')}</div>
        <fieldset class="size-finder__fieldset">
          <legend class="size-finder__label">${c.fitQuestion}</legend>
          <div class="size-finder__segments">${segments}</div>
          <p class="size-finder__hint" data-hint aria-live="polite">${esc(this.#hintFor('as_designed'))}</p>
        </fieldset>
        <button type="submit" class="button size-finder__submit">${c.submit}</button>
        <p class="size-finder__fineprint">${c.fineprint}</p>
        <p class="size-finder__status" data-status role="status" hidden></p>
      </form>`;
  }

  #chartView(message = '') {
    const c = this.copy;
    return `
      <section class="size-finder__view" data-view="chart" hidden>
        ${message ? `<p>${message}</p>` : ''}
        ${this.#chartTable()}
        ${message ? '' : `<div class="size-finder__actions"><button type="button" class="size-finder__link" on:click="/editDetails">${c.startFinder}</button></div>`}
      </section>`;
  }

  #chartTable(recommended = null) {
    const c = this.copy;
    const sizes = this.#profile?.sizes ?? [];
    if (!sizes.length) return '';
    const columns = ['chestHalf', 'hemHalf', 'length', 'sleeve', 'shoulder'].filter((key) => sizes.some((s) => s[key] != null));
    const heads = {
      chestHalf: c.columns.chest,
      hemHalf: c.columns.hem,
      length: c.columns.length,
      sleeve: c.columns.sleeve,
      shoulder: c.columns.shoulder,
    };
    const tolerance = this.#profile.tolerance;

    return `
      <table class="size-finder__table">
        <thead><tr><th scope="col">${c.columns.size}</th>${columns.map((k) => `<th scope="col">${heads[k]}</th>`).join('')}</tr></thead>
        <tbody>
          ${sizes
            .map(
              (s) => `<tr${s.label === recommended ? ' data-recommended' : ''}>
                <td>${esc(s.label)}</td>${columns.map((k) => `<td>${s[k] ?? '–'}</td>`).join('')}
              </tr>`
            )
            .join('')}
        </tbody>
      </table>
      ${tolerance ? `<p class="size-finder__disclaimer">${c.tolerance(tolerance)}</p>` : ''}`;
  }

  /** One sentence the shopper can act on: the size and how it will sit. */
  #headline(result, label) {
    const h = this.copy.headline;
    const fit = result.zones.chest.fit;
    const has = (flag) => result.flags.includes(flag);
    if (has('preference_unreachable')) {
      if (has('below_size_range')) return h.unreachableRoomy(label);
      return has('fit_limit_unknown') ? h.unreachableStretch(label) : h.unreachableClose(label);
    }
    if (has('below_size_range')) return h.belowRange(label);
    if (has('above_size_range')) return h.aboveRange(label);
    if (fit === 0) return h.asDesigned(label, esc(this.#profile.hints?.as_designed ?? ''));
    if (fit < 0) return result.preference === 'tighter' ? h.closerAsked(label) : h.closer(label);
    return result.preference === 'looser' ? h.roomierAsked(label) : h.roomier(label);
  }

  /** At most one note on the first screen: the one that matters most for the purchase. */
  #mainNote(result, label) {
    const n = this.copy.notes;
    const has = (flag) => result.flags.includes(flag);
    const runsLong = has('length_long') || has('sleeve_long');
    if (has('recommended_sold_out')) return n.soldOut(label);
    if (has('preference_unreachable')) return n.checkChart;
    if (has('fit_limit_unknown')) return n.stretchUnknown;
    if (result.edge === 'smallest' && runsLong) return n.smallestLong(label);
    if (has('sleeve_short')) return n.sleeveShort;
    if (has('length_short')) return n.lengthShort;
    if (has('length_long')) return n.lengthLong;
    if (has('sleeve_long')) return n.sleeveLong;
    if (has('outside_body_model')) return n.outsideModel;
    return '';
  }

  #renderResult(result) {
    const c = this.copy;
    const view = this.#view('result');

    if (result.status !== 'ok') {
      view.innerHTML = `
        <div class="size-finder__verdict">
          <div class="size-finder__size" aria-hidden="true">–</div>
          <div><p class="size-finder__kicker">${c.suggested}</p><p class="size-finder__headline">${c.noFitTitle}</p></div>
        </div>
        <p class="size-finder__note">${c.noFit}</p>
        <details class="size-finder__more" open><summary>${c.chart}</summary>${this.#chartTable()}</details>
        <div class="size-finder__actions"><button type="button" class="size-finder__link" on:click="/editDetails">${c.edit}</button></div>`;
      return;
    }

    const { size, alternative } = result;
    const label = esc(size);
    const sizeData = this.#profile.sizes.find((s) => s.label === size);
    const note = this.#mainNote(result, label);

    const altData = alternative && this.#profile.sizes.find((s) => s.label === alternative.size);
    const selectButton = sizeData.available
      ? `<button type="button" class="button size-finder__select" data-size="${label}" on:click="/selectSize">${c.select(label)}</button>`
      : `<button type="button" class="button size-finder__select" disabled>${c.soldOutButton(label)}</button>`;
    const altButton = altData?.available
      ? `<button type="button" class="size-finder__link" data-size="${esc(altData.label)}" on:click="/selectSize">${c.alternative[alternative.direction](esc(altData.label))}</button>`
      : '';

    view.innerHTML = `
      <div class="size-finder__verdict">
        <div class="size-finder__size" data-chars="${Math.min(size.length, 5)}" aria-hidden="true">${label}</div>
        <div>
          <p class="size-finder__kicker">${result.flags.includes('preference_unreachable') ? c.closest : c.suggested}<span class="visually-hidden">: ${label}</span></p>
          <p class="size-finder__headline">${this.#headline(result, label)}</p>
        </div>
      </div>
      ${note ? `<p class="size-finder__note">${note}</p>` : ''}
      <div class="size-finder__actions">
        ${selectButton}
        ${altButton}
      </div>
      <details class="size-finder__more">
        <summary>${c.why}</summary>
        ${this.#analysis(result)}
      </details>
      <details class="size-finder__more">
        <summary>${c.chart}</summary>
        ${this.#chartTable(size)}
      </details>
      <div class="size-finder__footer">
        <button type="button" class="size-finder__link" on:click="/editDetails">${c.edit}</button>
        <button type="button" class="size-finder__link size-finder__link--subdued" on:click="/forgetDetails">${c.forget}</button>
      </div>
      <p class="size-finder__disclaimer">${c.disclaimer}</p>`;
  }

  /** The zone-by-zone breakdown behind "Why this size?". */
  #analysis(result) {
    const c = this.copy;
    const { zones } = result;
    const rows = [
      ['chest', c.chestFit[zones.chest.fit], null],
      ['length', c.lengthFit[zones.length.fit], c.landmark[zones.length.landmark]],
      zones.sleeve ? ['sleeve', c.lengthFit[zones.sleeve.fit], this.#profile.cuff === 'rib' ? c.notes.ribCuff : null] : null,
    ].filter(Boolean);

    const refs = this.#profile.referenceFits
      .filter((ref) => ref.kind === 'ideal' && ref.weight)
      .map((ref) => c.reference(ref.height, ref.weight, esc(ref.size)))
      .join(', ');
    return `
      <p class="size-finder__intro">${c.whyIntro(refs)}</p>
      <dl class="size-finder__spec">
        ${rows
          .map(
            ([zone, value, sub]) => `
          <div class="size-finder__spec-row">
            <dt>${c.rows[zone]}</dt>
            <dd>${value}</dd>
            ${meter(zones[zone].fit)}
            ${sub ? `<p class="size-finder__spec-sub">${sub}</p>` : ''}
          </div>`
          )
          .join('')}
      </dl>
      ${this.#profile.note ? `<p class="size-finder__note">${esc(this.#profile.note)}</p>` : ''}`;
  }

  #id(name) {
    return `${this.refs.dialog.getAttribute('aria-labelledby')}-${name}`;
  }
}

/** Five cells from "much closer / shorter" to "much roomier / longer"; the middle one is "as designed". */
function meter(fit) {
  const cells = [-2, -1, 0, 1, 2].map((step) => `<span class="size-finder__meter-cell"${step === fit ? ' data-active' : ''}></span>`);
  return `<div class="size-finder__meter" aria-hidden="true">${cells.join('')}</div>`;
}

function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
}

if (!customElements.get('size-finder-component')) {
  customElements.define('size-finder-component', SizeFinderComponent);
}
